import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Phone, MapPin, Clock, RefreshCw, AlertTriangle, UserCog,
  PackageCheck, CheckCircle2, HandHeart, X, Bell, BellOff, Camera, ArrowRightLeft, Undo2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  api, formatApiError, formatDate, formatDateTime, formatRelativeFromNow, formatClockTime, todayISODate, ORDER_STATUS,
} from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { PushNotificationButton } from "@/components/PushNotificationButton";
import { PushActivationBanner } from "@/components/PushActivationBanner";
import { StatusBadge } from "@/components/StatusBadge";
import { PhotoPicker } from "@/components/PhotoPicker";
import { OrderLines } from "@/components/OrderLines";
import { HandoffHistory } from "@/components/HandoffHistory";
import { OrderPaperPhoto } from "@/components/OrderPaperPhoto";
import { DeliveryPhotoButton } from "@/components/DeliveryPhotoButton";
import { useAlarm } from "@/hooks/useAlarm";

const TABS = [
  { key: "bekliyor",      label: "Bekleyen" },
  { key: "teslim_alindi", label: "Teslim Aldıklarım" },
  { key: "teslim_edildi", label: "Teslim Ettiklerim" },
];

// Bir siparişin teslim saati (delivery_time) bugünün gece yarısı geçtikten
// sonrasına düşüyorsa "ileri tarihli" sayılır. Bu tür siparişler kuryenin
// aktif "Bekleyen" listesinde teslim günü gelene kadar gösterilmez.
function isFutureDate(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const t = new Date();
  const tomorrow = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1);
  return d.getTime() >= tomorrow.getTime();
}

// Verilen tarihi (Date veya ISO string) yerel saat diliminde yyyy-mm-dd
// biçiminde döndürür. Teslim edilenler filtrelenirken kullanılır.
function toLocalISODate(value) {
  if (!value) return null;
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 10);
  } catch { return null; }
}

export default function CourierPanel() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("bekliyor");
  const [preview, setPreview] = useState(null);
  const [deliverFor, setDeliverFor] = useState(null); // order being delivered
  const [handoffFor, setHandoffFor] = useState(null); // order being handed off
  const [couriers, setCouriers] = useState([]);
  const [mute, setMute] = useState(false);
  // "Teslim Ettiklerim" sekmesi için tek-gün tarih filtresi (varsayılan: bugün).
  // ISO yyyy-mm-dd biçiminde tutulur; sadece bu güne ait teslimler listelenir.
  const [deliveredDate, setDeliveredDate] = useState(todayISODate());
  const [, tick] = useState(0);

  const fetchOrders = async () => {
    try {
      // Aynı akıllı filtreleme — payload'ı küçük tutmak için seçili günün
      // teslimlerini + tüm aktif siparişleri iste.
      const startLocal = new Date(`${deliveredDate}T00:00:00`);
      const endLocal   = new Date(startLocal.getTime() + 24 * 60 * 60 * 1000);
      const params = new URLSearchParams({
        delivered_from: startLocal.toISOString(),
        delivered_to:   endLocal.toISOString(),
      });
      const { data } = await api.get(`/orders?${params.toString()}`);
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Kurye paneli: sekme görünür olduğunda 12 saniyede bir yenile.
  // Arka plana atıldığında polling durur. deliveredDate değişince de yeniden çeker.
  useEffect(() => {
    let timer;
    const start = () => {
      fetchOrders();
      timer = setInterval(fetchOrders, 12000);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = undefined; } };
    const onVis = () => {
      if (document.visibilityState === "visible") { stop(); start(); }
      else { stop(); }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveredDate]);

  useEffect(() => {
    // Aktif kurye listesi — devir modalı için
    api.get("/users/couriers").then(({ data }) => setCouriers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => tick((v) => v + 1), 20000);
    return () => clearInterval(t);
  }, []);

  // BİLDİRİME DOKUNUNCA DERİN LİNK — service worker `/courier?order=<id>` açar.
  // Bir kere ele alındıktan sonra useRef sentinel ile tekrar tetiklenmez.
  const handledDeepLinkRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("order");
    if (!focusId || handledDeepLinkRef.current === focusId) return;
    const target = orders.find((o) => o.id === focusId);
    if (!target) return;
    handledDeepLinkRef.current = focusId;
    if (target.status === "bekliyor")           setTab("bekliyor");
    else if (target.status === "teslim_alindi") setTab("teslim_alindi");
    else if (target.status === "teslim_edildi") setTab("teslim_edildi");
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="courier-order-${focusId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-4", "ring-amber-400");
        setTimeout(() => el.classList.remove("ring-4", "ring-amber-400"), 2500);
      }
      window.history.replaceState({}, "", "/courier");
    }, 250);
  }, [loading, orders]);

  // Bekliyor: ALL bekliyor siparişleri (her kurye görür — claim sadece niyet, kilit değil).
  // Ancak "ileri tarihli" olanlar teslim günü gelene kadar aktif listede GİZLENİR.
  // teslim_alindi: sadece ben teslim aldıysam
  // teslim_edildi: sadece ben teslim aldıysam VE seçili günde (varsayılan: bugün)
  //                teslim edildiyse — en yeni teslim en üstte
  const groups = useMemo(() => {
    const sortByUrgency = (a, b) => {
      if (!!a.is_late !== !!b.is_late) return a.is_late ? -1 : 1;
      if (!!a.warning !== !!b.warning) return a.warning ? -1 : 1;
      return new Date(a.delivery_time) - new Date(b.delivery_time);
    };
    const deliveredMine = orders.filter(
      (o) => o.status === "teslim_edildi" && o.picked_up_by_id === user?.id,
    );
    const deliveredForSelectedDay = deliveredMine
      .filter((o) => {
        const ts = o.delivered_at || o.delivery_time;
        return toLocalISODate(ts) === deliveredDate;
      })
      .sort((a, b) => {
        const ta = new Date(a.delivered_at || a.delivery_time).getTime();
        const tb = new Date(b.delivered_at || b.delivery_time).getTime();
        return tb - ta; // en yeni teslim üstte
      });
    return {
      bekliyor:      [...orders.filter((o) => o.status === "bekliyor" && !isFutureDate(o.delivery_time))].sort(sortByUrgency),
      teslim_alindi: orders.filter((o) => o.status === "teslim_alindi" && o.picked_up_by_id === user?.id),
      teslim_edildi: deliveredForSelectedDay,
    };
  }, [orders, user, deliveredDate]);

  const hasWarning = useMemo(() => groups.bekliyor.some((o) => o.warning), [groups.bekliyor]);
  useAlarm(hasWarning && !mute);

  const visible = groups[tab] || [];

  const act = async (id, action) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/orders/${id}/${action}`);
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      const msg = action === "claim" ? "Niyetiniz kaydedildi"
        : action === "pickup" ? "Teslim alındı — sorumluluk sizde"
        : action === "release" ? "Sipariş bekleyenler havuzuna geri bırakıldı"
        : "Teslim edildi";
      toast.success(msg);
      fetchOrders();
    } catch (e) {
      toast.error(formatApiError(e));
      fetchOrders();
    } finally {
      setBusyId(null);
    }
  };

  const handleDeliver = async (orderId, imageFile) => {
    setBusyId(orderId);
    try {
      const fd = new FormData();
      if (imageFile) fd.append("image", imageFile);
      const { data } = await api.post(`/orders/${orderId}/deliver`, fd);
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      toast.success(imageFile ? "Teslim edildi — fotoğraf eklendi" : "Teslim edildi");
      setDeliverFor(null);
      fetchOrders();
    } catch (e) {
      toast.error(formatApiError(e));
      fetchOrders();
    } finally {
      setBusyId(null);
    }
  };

  const handleHandoff = async (orderId, toCourierId) => {
    setBusyId(orderId);
    try {
      const { data } = await api.post(`/orders/${orderId}/handoff`, { to_courier_id: toCourierId });
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      toast.success(`Devredildi → ${data.picked_up_by_name}`);
      setHandoffFor(null);
      fetchOrders();
    } catch (e) {
      toast.error(formatApiError(e));
      fetchOrders();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title={user?.display_name || "Kurye"}
        subtitle="Kurye Paneli"
        right={
          <>
            <button
              data-testid="toggle-mute"
              onClick={() => setMute((m) => !m)}
              className={`p-2 border-2 transition-colors ${
                mute ? "bg-zinc-100 text-zinc-500 border-zinc-300" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-950"
              }`}
              aria-label={mute ? "Sesi aç" : "Sesi kapat"}
            >
              {mute ? <BellOff size={14} strokeWidth={2.5} /> : <Bell size={14} strokeWidth={2.5} />}
            </button>
            <button
              data-testid="refresh-button"
              onClick={fetchOrders}
              className="p-2 border-2 border-zinc-200 hover:border-zinc-950 transition-colors"
              aria-label="Yenile"
            >
              <RefreshCw size={14} strokeWidth={2.5} />
            </button>
            <PushNotificationButton />
          </>
        }
      />

      <div className="max-w-4xl mx-auto px-2 sm:px-4 pt-4">
        <PushActivationBanner />
        <div className="grid grid-cols-3 gap-2">
          {TABS.map((t) => {
            const count = groups[t.key]?.length || 0;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`py-2 px-1 border-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                  active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-950"
                }`}
              >
                {t.label}{" "}
                <span className={`font-mono ${active ? "text-white/80" : "text-zinc-500"}`}>({count})</span>
              </button>
            );
          })}
        </div>

        {tab === "teslim_edildi" && (
          <div className="mt-3 bg-white border-2 border-zinc-200 p-3 flex items-center gap-2 sm:gap-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 shrink-0">
              Gün
            </div>
            <input
              data-testid="courier-delivered-date-input"
              type="date"
              value={deliveredDate}
              max={todayISODate()}
              onChange={(e) => setDeliveredDate(e.target.value)}
              className="flex-1 min-w-0 brutalist-input !py-1.5 !text-sm font-bold tracking-tight text-zinc-950"
            />
            <button
              type="button"
              data-testid="courier-delivered-today-btn"
              onClick={() => setDeliveredDate(todayISODate())}
              className="shrink-0 px-3 py-1.5 border-2 border-zinc-300 hover:border-zinc-950 text-xs font-bold uppercase tracking-wider"
            >
              Bugün
            </button>
          </div>
        )}
      </div>

      {hasWarning && tab === "bekliyor" && (
        <div className="max-w-4xl mx-auto px-2 sm:px-4 mt-3">
          <div
            data-testid="warning-banner"
            className="p-3 bg-red-600 text-white border-2 border-red-700 brutal-shadow flex items-center gap-2 animate-pulse"
          >
            <AlertTriangle size={20} strokeWidth={2.5} />
            <div className="flex-1 text-xs font-black uppercase tracking-wider">
              Geciken sipariş var!
            </div>
            <button
              data-testid="dismiss-alarm"
              onClick={() => setMute(true)}
              className="px-2 py-1 bg-white text-red-700 text-[11px] font-bold uppercase tracking-wider"
            >
              Sesi Kapat
            </button>
          </div>
        </div>
      )}

      <main className="app-safe-bottom max-w-4xl mx-auto px-2 sm:px-4 py-4 pb-32">
        {loading ? (
          <Empty text="Yükleniyor…" />
        ) : visible.length === 0 ? (
          <Empty text={
            tab === "bekliyor"      ? "Şu an bekleyen sipariş yok." :
            tab === "teslim_alindi" ? "Teslim aldığın aktif sipariş yok." :
                                      (deliveredDate === todayISODate()
                                        ? "Bugün teslim ettiğin sipariş yok."
                                        : `${new Date(deliveredDate).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })} tarihinde teslim ettiğin sipariş yok.`)
          } />
        ) : (
          <ul className="space-y-4" data-testid="courier-orders-list">
            {visible.map((o, i) => (
              <OrderCard
                key={o.id}
                order={o}
                index={i}
                me={user}
                busy={busyId === o.id}
                onAct={act}
                onDeliver={(order) => setDeliverFor(order)}
                onHandoff={(order) => setHandoffFor(order)}
                onPreview={(src) => setPreview(src)}
              />
            ))}
          </ul>
        )}
      </main>

      {preview && (
        <div
          data-testid="courier-image-preview"
          className="fixed inset-0 z-50 bg-zinc-950/85 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <button onClick={() => setPreview(null)} className="absolute top-4 right-4 text-white">
            <X size={28} />
          </button>
          <img src={preview} alt="Önizleme" className="max-h-[85vh] max-w-full object-contain border-4 border-white" />
        </div>
      )}

      <DeliveryConfirmModal
        order={deliverFor}
        busy={busyId === deliverFor?.id}
        onClose={() => setDeliverFor(null)}
        onSubmit={handleDeliver}
      />

      <HandoffModal
        order={handoffFor}
        couriers={couriers}
        me={user}
        busy={busyId === handoffFor?.id}
        onClose={() => setHandoffFor(null)}
        onSubmit={handleHandoff}
      />
    </div>
  );
}

function DeliveryConfirmModal({ order, busy, onClose, onSubmit }) {
  const [file, setFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);

  useEffect(() => {
    if (!order) {
      setFile(null);
      setPreviewSrc(null);
    }
  }, [order]);

  if (!order) return null;

  return (
    <div
      data-testid="deliver-modal"
      className="fixed inset-0 z-50 bg-zinc-950/70 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white border-2 border-zinc-950 brutal-shadow-dark w-full sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-zinc-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-600 text-white flex items-center justify-center">
              <CheckCircle2 strokeWidth={3} size={16} />
            </div>
            <h2 className="text-base font-black tracking-tight text-zinc-950 uppercase">Teslim Onayı</h2>
          </div>
          <button data-testid="close-deliver-modal" onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-950">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-zinc-50 border-2 border-zinc-200 px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Müşteri</div>
            <div className="text-base font-bold text-zinc-950 break-words">{order.full_name}</div>
            <div className="text-xs text-zinc-600 break-words">{order.address}</div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Camera size={14} strokeWidth={2.5} className="text-zinc-700" />
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-700">
                Teslim Fotoğrafı (Opsiyonel)
              </div>
            </div>
            <PhotoPicker
              file={file}
              preview={previewSrc}
              onChange={(f, p) => { setFile(f); setPreviewSrc(p); }}
              testIdPrefix="deliver-photo"
            />
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-2">
              {file ? "Fotoğraf hazır — Teslim Et'e basınca yüklenir" : "Fotoğraf eklemeden de teslim edebilirsiniz"}
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              data-testid="cancel-deliver"
              onClick={onClose}
              disabled={busy}
              className="flex-1 py-3 border-2 border-zinc-300 text-zinc-700 text-xs font-bold uppercase tracking-widest hover:border-zinc-950 hover:text-zinc-950 transition-colors disabled:opacity-60"
            >
              Vazgeç
            </button>
            <button
              type="button"
              data-testid="confirm-deliver"
              onClick={() => onSubmit(order.id, file)}
              disabled={busy}
              className="flex-1 bg-emerald-600 text-white text-xs font-black uppercase tracking-widest py-3 border-b-4 border-emerald-900 active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-60"
            >
              {busy ? "Gönderiliyor…" : "Teslim Et"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HandoffModal({ order, couriers, me, busy, onClose, onSubmit }) {
  const [target, setTarget] = useState(null);

  useEffect(() => { if (!order) setTarget(null); }, [order]);

  if (!order) return null;

  const options = (couriers || []).filter((c) => c.id !== me?.id);

  return (
    <div
      data-testid="handoff-modal"
      className="fixed inset-0 z-50 bg-zinc-950/70 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white border-2 border-zinc-950 brutal-shadow-dark w-full sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-zinc-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-violet-700 text-white flex items-center justify-center">
              <ArrowRightLeft strokeWidth={3} size={16} />
            </div>
            <h2 className="text-base font-black tracking-tight text-zinc-950 uppercase">Siparişi Devret</h2>
          </div>
          <button data-testid="close-handoff-modal" onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-950">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-zinc-50 border-2 border-zinc-200 px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Müşteri</div>
            <div className="text-base font-bold text-zinc-950 break-words">{order.full_name}</div>
            <div className="text-xs text-zinc-600 break-words">{order.address}</div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Devredilecek Kurye
            </div>
            {options.length === 0 ? (
              <div
                data-testid="handoff-no-couriers"
                className="border-2 border-dashed border-zinc-300 p-4 text-center font-mono text-[11px] uppercase tracking-wider text-zinc-500"
              >
                Devredebileceğin başka aktif kurye yok
              </div>
            ) : (
              <ul className="space-y-2 max-h-[40vh] overflow-y-auto" data-testid="handoff-courier-list">
                {options.map((c) => {
                  const active = target === c.id;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        data-testid={`handoff-pick-${c.id}`}
                        onClick={() => setTarget(c.id)}
                        className={`w-full flex items-center justify-between gap-3 px-3 py-3 border-2 transition-colors text-left ${
                          active
                            ? "bg-violet-900 border-violet-900 text-white"
                            : "bg-white border-zinc-200 hover:border-violet-700 text-zinc-900"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-bold break-words">{c.display_name}</div>
                          <div className={`font-mono text-[10px] uppercase tracking-wider ${active ? "text-white/80" : "text-zinc-500"}`}>
                            {c.username}
                          </div>
                        </div>
                        {active && (
                          <span className="font-mono text-[10px] uppercase tracking-widest bg-white text-violet-900 px-2 py-0.5">
                            Seçili
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              data-testid="cancel-handoff"
              onClick={onClose}
              disabled={busy}
              className="flex-1 py-3 border-2 border-zinc-300 text-zinc-700 text-xs font-bold uppercase tracking-widest hover:border-zinc-950 hover:text-zinc-950 transition-colors disabled:opacity-60"
            >
              Vazgeç
            </button>
            <button
              type="button"
              data-testid="confirm-handoff"
              onClick={() => target && onSubmit(order.id, target)}
              disabled={busy || !target}
              className="flex-1 bg-violet-700 text-white text-xs font-black uppercase tracking-widest py-3 border-b-4 border-violet-900 active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? "Devrediliyor…" : "Devret"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order: o, index, me, busy, onAct, onDeliver, onHandoff, onPreview }) {
  const warning = o.warning && o.status !== "teslim_edildi";
  const isLate = o.is_late && o.status !== "teslim_edildi";
  const iClaimedIt = o.claimed_by_id === me?.id;
  const iPickedUp = o.picked_up_by_id === me?.id;
  const borderColor = isLate ? "border-red-600" : warning ? "border-amber-500" : "border-zinc-950";
  const bgTone      = isLate ? "bg-red-50" : warning ? "bg-amber-50" : "bg-white";

  return (
    <li
      style={{ animationDelay: `${index * 30}ms` }}
      className={`slide-up border-2 brutal-shadow-dark ${bgTone} ${borderColor}`}
      data-testid={`courier-order-${o.id}`}
    >
      <div className="flex flex-col">
        {/* DELIVERY TIME - prominent top bar */}
        <div
          data-testid={`co-time-bar-${o.id}`}
          className={`px-5 py-3 border-b-2 flex items-center justify-between gap-3 ${
            o.is_late && o.status !== "teslim_edildi"
              ? "bg-red-600 border-red-700 text-white"
              : warning
              ? "bg-amber-400 border-amber-600 text-zinc-950"
              : "bg-zinc-950 border-zinc-950 text-white"
          }`}
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-80">Teslim Saati</div>
          <div className="text-3xl sm:text-4xl font-black tabular-nums tracking-tight leading-none flex items-center gap-2">
            <Clock size={22} strokeWidth={3} />
            {formatClockTime(o.delivery_time)}
          </div>
        </div>

        <OrderPaperPhoto
          orderId={o.id}
          hasImage={!!o.has_image}
          alt={o.full_name}
          onPreview={onPreview}
          testIdPrefix={`courier-order-${o.id}`}
          size="md"
        />

        <div className="p-5 flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <StatusBadge status={o.status} warning={warning} testId={`co-status-${o.id}`} />
          {o.is_late && o.status !== "teslim_edildi" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest" data-testid={`co-late-${o.id}`}>
              GECİKTİ
            </span>
          )}
          {!o.is_late && warning && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-400 text-zinc-950 text-[10px] font-bold uppercase tracking-widest" data-testid={`co-warning-${o.id}`}>
              30 DK
            </span>
          )}
        </div>

        {/* CUSTOMER NAME row: single line, fluid font, no ellipsis.
            Tarih ve "kalan süre" alt satıra ayrılır — isimle hizalanmaz. */}
        <div className="mb-3 space-y-1">
          <div
            className="font-black tracking-tight text-zinc-950 leading-tight whitespace-nowrap overflow-hidden"
            style={{ fontSize: "clamp(0.8125rem, 5vw, 1.875rem)" }}
          >
            {o.full_name}
          </div>
          <div className={`flex items-baseline justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.18em] ${o.is_late ? "text-red-600" : warning ? "text-amber-700" : "text-zinc-500"}`}>
            <span className="whitespace-nowrap">{formatDate(o.delivery_time)}</span>
            <span className="whitespace-nowrap">{formatRelativeFromNow(o.delivery_time)}</span>
          </div>
        </div>

        <div className="space-y-2">
          <a href={`tel:${o.phone}`} className="flex items-center gap-2 text-zinc-900 font-mono text-sm hover:underline">
            <Phone size={15} strokeWidth={2.5} className="text-zinc-500" />
            {o.phone}
          </a>
          <div className="flex items-start gap-2 text-zinc-900 text-sm">
            <MapPin size={15} strokeWidth={2.5} className="text-zinc-500 mt-0.5 shrink-0" />
            <span className="break-words">{o.address}</span>
          </div>
        </div>

        <OrderLines order={o} testIdPrefix={`courier-order-${o.id}`} />

        {/* Responsibility chain */}
        <div className="mt-4 grid grid-cols-1 gap-2">
          <RespRow
            testId={`resp-creator-${o.id}`}
            icon={<UserCog size={12} strokeWidth={2.5} />}
            label="Siparişi İşleyen"
            who={o.created_by_name}
            at={o.created_at}
            highlightMe={false}
          />
          <RespRow
            testId={`resp-claim-${o.id}`}
            icon={<HandHeart size={12} strokeWidth={2.5} />}
            label="Götürecek Kişi"
            who={o.claimed_by_name}
            at={o.claimed_at}
            highlightMe={iClaimedIt}
          />
          <RespRow
            testId={`resp-pickup-${o.id}`}
            icon={<PackageCheck size={12} strokeWidth={2.5} />}
            label="Teslim Alan"
            who={o.picked_up_by_name}
            at={o.picked_up_at}
            highlightMe={iPickedUp}
          />
          <RespRow
            testId={`resp-deliver-${o.id}`}
            icon={<CheckCircle2 size={12} strokeWidth={2.5} />}
            label="Teslim Eden"
            who={o.delivered_at ? o.picked_up_by_name : null}
            at={o.delivered_at}
            highlightMe={iPickedUp && o.delivered_at}
          />
        </div>

        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Açılış {formatClockTime(o.created_at)}
        </div>

        <HandoffHistory history={o.handoff_history} testIdPrefix={`courier-order-${o.id}`} />

        {/* Action buttons */}
        <div className="mt-5 space-y-2">
          {o.status === "bekliyor" && !iClaimedIt && !o.claimed_by_id && (
            <ActionButton
              testId={`claim-${o.id}`}
              onClick={() => onAct(o.id, "claim")}
              disabled={busy}
              icon={<HandHeart strokeWidth={2.5} />}
              label="Ben Götüreceğim"
              color={ORDER_STATUS.bekliyor.bg}
              dark
            />
          )}
          {o.status === "bekliyor" && iClaimedIt && (
            <div
              data-testid={`claim-mine-${o.id}`}
              className="w-full text-center py-2 bg-amber-100 border-2 border-amber-300 text-amber-900 text-xs font-bold uppercase tracking-widest"
            >
              Götüreceksin
            </div>
          )}
          {o.status === "bekliyor" && (
            <ActionButton
              testId={`pickup-${o.id}`}
              onClick={() => onAct(o.id, "pickup")}
              disabled={busy}
              icon={<PackageCheck strokeWidth={2.5} />}
              label="Teslim Aldım"
              color={ORDER_STATUS.teslim_alindi.bg}
            />
          )}
          {o.status === "teslim_alindi" && iPickedUp && (
            <ActionButton
              testId={`deliver-${o.id}`}
              onClick={() => onDeliver(o)}
              disabled={busy}
              icon={<CheckCircle2 strokeWidth={2.5} />}
              label="Teslim Ettim"
              color={ORDER_STATUS.teslim_edildi.bg}
            />
          )}
          {o.status === "teslim_alindi" && iPickedUp && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid={`handoff-${o.id}`}
                onClick={() => onHandoff(o)}
                disabled={busy}
                className="flex items-center justify-center gap-2 bg-white border-2 border-violet-700 text-violet-800 text-sm font-black uppercase tracking-widest py-3 hover:bg-violet-50 active:translate-y-[1px] transition-all disabled:opacity-60"
              >
                <ArrowRightLeft size={16} strokeWidth={2.5} />
                Devret
              </button>
              <button
                type="button"
                data-testid={`release-${o.id}`}
                onClick={() => {
                  if (window.confirm("Siparişi tekrar bekleyenler havuzuna bırakmak istediğinize emin misiniz?")) {
                    onAct(o.id, "release");
                  }
                }}
                disabled={busy}
                className="flex items-center justify-center gap-2 bg-white border-2 border-amber-600 text-amber-700 text-sm font-black uppercase tracking-widest py-3 hover:bg-amber-50 active:translate-y-[1px] transition-all disabled:opacity-60"
              >
                <Undo2 size={16} strokeWidth={2.5} />
                Geri Bırak
              </button>
            </div>
          )}
          {o.status === "teslim_alindi" && !iPickedUp && (
            <div
              data-testid={`locked-pickup-${o.id}`}
              className="w-full text-center py-3 bg-zinc-100 border-2 border-zinc-300 text-zinc-600 text-xs font-bold uppercase tracking-widest"
            >
              {o.picked_up_by_name} Teslim Aldı
            </div>
          )}
          {o.status === "teslim_edildi" && (
            <>
              <DeliveryPhotoButton
                orderId={o.id}
                hasDeliveryImage={!!o.has_delivery_image}
                onPreview={onPreview}
                testId={`delivery-photo-thumb-${o.id}`}
              />
              <div className="w-full text-center py-3 bg-emerald-50 border-2 border-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-widest">
                Tamamlandı
              </div>
            </>
          )}
        </div>
        </div>
      </div>
    </li>
  );
}

function RespRow({ icon, label, who, at, highlightMe, testId }) {
  const filled = !!who;
  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-3 px-3 py-2 border ${
        highlightMe ? "bg-zinc-950 border-zinc-950 text-white"
        : filled ? "bg-zinc-50 border-zinc-200"
        : "bg-white border-dashed border-zinc-200"
      }`}
    >
      <div className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] w-[96px] shrink-0 ${
        highlightMe ? "text-white/80" : "text-zinc-500"
      }`}>
        {icon}
        {label}
      </div>
      <div className={`flex-1 text-sm font-bold break-words min-w-0 ${
        highlightMe ? "text-white" : filled ? "text-zinc-900" : "text-zinc-400"
      }`}>
        {who || "—"}
        {highlightMe && who && <span className="ml-1 font-mono text-[10px] opacity-80">(SEN)</span>}
      </div>
      {at && (
        <div className={`font-mono text-[10px] shrink-0 ${highlightMe ? "text-white/80" : "text-zinc-500"}`}>
          {formatClockTime(at)}
        </div>
      )}
    </div>
  );
}

function ActionButton({ onClick, disabled, icon, label, color, testId, dark }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{ backgroundColor: color, color: dark ? "#09090b" : "#ffffff" }}
      className="w-full flex items-center justify-center gap-3 text-base font-black uppercase tracking-widest py-5 border-b-4 border-zinc-950 active:border-b-0 active:translate-y-[4px] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {icon}
      {label}
    </button>
  );
}

function Empty({ text }) {
  return (
    <div className="border-2 border-dashed border-zinc-300 bg-white p-10 text-center">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">{text}</div>
    </div>
  );
}
